// assets/xero-single-hero.js
// Behavior for sections/xero-single-hero.liquid (DEV-3206 prototype, DEV-3210
// production). Depends on assets/xero-utils.js (xsReducedMotion, xsVideoToggle)
// — loaded deferred at the end of <body> via snippets/script-tags.liquid.
//
// Loading contract (DEV-3210):
// - The poster <img> is in the HTML and paints on its own; nothing here gates it.
//   It is the LCP element and must be the only hero bytes on the wire while LCP
//   is being decided.
// - Autoplay starts the video fetch once the poster has loaded AND the section
//   is in view. We do not wait for window `load`, DOMContentLoaded, or
//   requestIdleCallback — those wait on the rest of the page (apps, pixels,
//   other deferred scripts) and made time-to-play look the same with or without
//   network throttle. fetch() uses priority: 'low' so the MP4 stays behind
//   remaining critical work.
// - The matching MP4 is downloaded IN FULL with fetch() -> Blob, handed to the
//   <video> as an object URL, and played. Because every byte is local the first
//   `playing` event fires within a frame and is the swap trigger: the section
//   gets .is-video-ready and the CSS fades the video in over the image.
// - If fetch() fails (network / CORS), fall back to streaming via video.src and
//   reveal only once `buffered` covers the whole duration. A 20s abort keeps
//   the poster if the download never finishes.
// - Save-Data: no video, no play control. Slow connections skip autoplay
//   (poster Resource Timing, else navigator.connection). Reduced-motion
//   visitors keep the image; the play control still lets them opt in.
// - IntersectionObserver only gates the first autoplay (in view). After that
//   the muted loop keeps running, including off-screen. A manual pause stays
//   paused because nothing calls play() again.
// - Re-initializes on xs:section:init and cleans up (abort fetch, revoke object
//   URL) on xs:section:destroy.

(function () {
  'use strict';

  // The <script> tag lives in the section markup, so guard against the file
  // executing twice if the section is ever placed on a page more than once.
  if (window.__xsSingleHeroInit) return;
  window.__xsSingleHeroInit = true;

  var SELECTOR = '[data-section-type="xero-single-hero"]';
  var DESKTOP_MQ = '(min-width: 1024px)';
  var MIN_THROUGHPUT_MBPS = 1.5;
  var FETCH_TIMEOUT_MS = 20000;

  var initialized = new WeakSet();
  var motionMq = window.matchMedia('(prefers-reduced-motion: reduce)');

  function reducedMotion() {
    if (typeof window.xsReducedMotion === 'function') return window.xsReducedMotion();
    return motionMq.matches;
  }

  function saveData() {
    var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return !!(conn && conn.saveData);
  }

  /* ---------- Poster ---------- */

  function posterImage(root) {
    return root.querySelector('.xs-shero__media img');
  }

  // Run cb once the poster has finished (loaded or failed). No poster: run now.
  function whenPosterSettled(root, cb) {
    var img = posterImage(root);
    if (!img || img.complete) {
      cb();
      return;
    }
    var done = false;
    function settle() {
      if (done) return;
      done = true;
      cb();
    }
    img.addEventListener('load', settle, { once: true });
    img.addEventListener('error', settle, { once: true });
  }

  /* ---------- Slow-connection guard ---------- */

  function posterResourceEntry(img) {
    if (!img || typeof performance.getEntriesByType !== 'function') return null;
    var src = img.currentSrc || img.src;
    if (!src) return null;
    var entries = performance.getEntriesByType('resource');
    for (var i = entries.length - 1; i >= 0; i--) {
      if (entries[i].name === src) return entries[i];
    }
    return null;
  }

  // Mbps from the poster's own download. transferSize is 0 for cached files
  // and for cross-origin CDN responses without Timing-Allow-Origin (Shopify
  // images usually live on cdn.shopify.com), so this often returns null.
  function posterThroughputMbps(img) {
    var entry = posterResourceEntry(img);
    if (!entry) return { mbps: null, reason: 'no-timing-entry' };
    if (!entry.transferSize) {
      return {
        mbps: null,
        reason: 'transfer-size-0',
        duration: entry.duration,
        transferSize: entry.transferSize
      };
    }
    if (entry.duration < 8) {
      return {
        mbps: null,
        reason: 'duration-too-small',
        duration: entry.duration,
        transferSize: entry.transferSize
      };
    }
    return {
      mbps: (entry.transferSize * 8) / (entry.duration / 1000) / 1e6,
      reason: 'poster-timing',
      duration: entry.duration,
      transferSize: entry.transferSize
    };
  }

  function connectionFallback() {
    var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return { skip: false, reason: 'no-connection-api' };
    if (typeof conn.downlink === 'number' && conn.downlink > 0 && conn.downlink < MIN_THROUGHPUT_MBPS) {
      return { skip: true, reason: 'downlink', downlink: conn.downlink, effectiveType: conn.effectiveType };
    }
    var type = conn.effectiveType;
    if (type === 'slow-2g' || type === '2g' || type === '3g') {
      return { skip: true, reason: 'effectiveType', downlink: conn.downlink, effectiveType: type };
    }
    return { skip: false, reason: 'connection-ok', downlink: conn.downlink, effectiveType: type };
  }

  // Autoplay-only, except Save-Data which also blocks the toggle.
  function shouldSkipAutoplay(root) {
    if (saveData()) return { skip: true, why: 'save-data' };

    var measured = posterThroughputMbps(posterImage(root));
    if (measured.mbps != null) {
      return {
        skip: measured.mbps < MIN_THROUGHPUT_MBPS,
        why: measured.reason,
        mbps: measured.mbps
      };
    }
    var fallback = connectionFallback();
    return {
      skip: fallback.skip,
      why: fallback.reason,
      downlink: fallback.downlink,
      effectiveType: fallback.effectiveType
    };
  }

  /* ---------- Video: rendition selection ---------- */

  // Among the <source data-src> elements for the cut that matches the viewport,
  // pick the smallest rendition that is still at least as wide as the viewport
  // in device pixels (capped at 1920 — nobody needs 4K for a background loop).
  // Falls back to the largest available, then to any cut at all.
  function pickSource(video) {
    var all = Array.prototype.slice.call(video.querySelectorAll('source[data-src]'));
    if (!all.length) return null;

    var want = window.matchMedia(DESKTOP_MQ).matches ? 'desktop' : 'mobile';
    var cut = all.filter(function (s) { return s.getAttribute('data-xs-media') === want; });
    if (!cut.length) cut = all; // only one cut uploaded: use it everywhere

    var need = Math.min(1920, Math.round(window.innerWidth * (window.devicePixelRatio || 1)));
    var width = function (s) { return parseInt(s.getAttribute('data-width'), 10) || 0; };
    cut.sort(function (a, b) { return width(a) - width(b); });

    for (var i = 0; i < cut.length; i++) {
      if (width(cut[i]) >= need) return cut[i];
    }
    return cut[cut.length - 1];
  }

  /* ---------- Video: background download + swap ---------- */

  function setToggleLoading(root, loading) {
    var toggle = root.querySelector('[data-xs-video-toggle]');
    if (!toggle) return;
    if (loading) {
      toggle.dataset.state = 'loading';
      toggle.setAttribute('aria-busy', 'true');
    } else {
      toggle.removeAttribute('aria-busy');
      if (toggle.dataset.state === 'loading') toggle.dataset.state = 'paused';
    }
  }

  function reveal(root) {
    root.classList.add('is-video-ready');
  }

  // Streaming fallback (fetch() failed): play hidden, stay on the poster until
  // the element has the whole file. No timeout — a half-buffered swap is the
  // glitch this section exists to avoid. If the file never finishes, the
  // poster stays. Resets to 0 so the visible loop starts from the top.
  function revealWhenFullyBuffered(root, video) {
    function fullyBuffered() {
      if (!video.duration || !isFinite(video.duration) || !video.buffered.length) return false;
      return video.buffered.end(video.buffered.length - 1) >= video.duration;
    }
    function tryReveal() {
      if (root.classList.contains('is-video-ready')) return cleanup();
      if (video.paused) return; // not playing yet; `playing` will re-check
      if (!fullyBuffered()) return;
      try { video.currentTime = 0; } catch (_) { /* not seekable yet */ }
      reveal(root);
      cleanup();
    }
    function cleanup() {
      video.removeEventListener('progress', tryReveal);
      video.removeEventListener('playing', tryReveal);
    }
    video.addEventListener('progress', tryReveal);
    video.addEventListener('playing', tryReveal);
  }

  function clearFetchTimeout(video) {
    if (video.__xsSheroTimeout) {
      clearTimeout(video.__xsSheroTimeout);
      video.__xsSheroTimeout = null;
    }
  }

  // Resolve true once the <video> has a playable src (Blob preferred, stream
  // fallback), false if there is nothing to load or the load was aborted.
  // Idempotent: concurrent callers share one promise.
  function ensureVideo(root, video) {
    if (video.__xsSheroReady) return Promise.resolve(true);
    if (video.__xsSheroLoading) return video.__xsSheroLoading;

    if (saveData()) return Promise.resolve(false);

    var source = pickSource(video);
    if (!source) return Promise.resolve(false);

    var url = source.getAttribute('data-src');

    // With video.src set, <source> children are ignored anyway; drop them so
    // the browser can never fall through to a different rendition.
    video.querySelectorAll('source').forEach(function (s) { s.remove(); });

    var controller = ('AbortController' in window) ? new AbortController() : null;
    video.__xsSheroAbort = controller;
    setToggleLoading(root, true);

    if (controller) {
      video.__xsSheroTimeout = setTimeout(function () {
        try { controller.abort(); } catch (_) { /* already settled */ }
      }, FETCH_TIMEOUT_MS);
    }

    var fetchOpts = {
      mode: 'cors',
      credentials: 'omit',
      cache: 'force-cache',
      priority: 'low'
    };
    if (controller) fetchOpts.signal = controller.signal;

    var attempt;
    if (typeof window.fetch === 'function' && typeof URL.createObjectURL === 'function') {
      attempt = window.fetch(url, fetchOpts).then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.blob();
      }).then(function (blob) {
        clearFetchTimeout(video);
        if (video.__xsSheroDestroyed) return false;
        var objectUrl = URL.createObjectURL(blob);
        video.__xsSheroObjectUrl = objectUrl;
        video.preload = 'auto';
        video.src = objectUrl;
        // Bytes are local: the first `playing` is the swap.
        video.addEventListener('playing', function onPlaying() {
          video.removeEventListener('playing', onPlaying);
          reveal(root);
        });
        return true;
      });
    } else {
      attempt = Promise.reject(new Error('fetch/createObjectURL unavailable'));
    }

    video.__xsSheroLoading = attempt.catch(function (err) {
      clearFetchTimeout(video);
      if (video.__xsSheroDestroyed || (err && err.name === 'AbortError')) return false;
      // Fallback: let the element stream it, but keep it hidden until the
      // whole file is buffered.
      revealWhenFullyBuffered(root, video);
      video.preload = 'auto';
      video.src = url;
      return true;
    }).then(function (ok) {
      video.__xsSheroLoading = null;
      if (ok) video.__xsSheroReady = true;
      setToggleLoading(root, false);
      return ok;
    });

    return video.__xsSheroLoading;
  }

  function playIfAllowed(root, video) {
    if (video.__xsUserPaused) return;
    if (reducedMotion()) return;
    ensureVideo(root, video).then(function (ok) {
      if (!ok || video.__xsUserPaused) return;
      video.play().catch(function () { /* autoplay blocked by the browser; toggle still works */ });
    });
  }

  function bindToggle(root, video, toggle) {
    if (!toggle || toggle.__xsSheroClickBound) return;
    toggle.__xsSheroClickBound = true;

    // Registered before xsVideoToggle binds its own click handler, so an
    // explicit play on a not-yet-loaded video (reduced motion / skipped
    // autoplay) downloads the file first. xsVideoToggle's own play() rejects
    // harmlessly while there is no src; we play once the download lands.
    toggle.addEventListener('click', function () {
      if (!video.paused) return;
      ensureVideo(root, video).then(function (ok) {
        if (ok && !video.__xsUserPaused) video.play().catch(function () {});
      });
    });

    if (typeof window.xsVideoToggle === 'function') window.xsVideoToggle(video, toggle);

    // xsVideoToggle mirrors play/pause events onto data-state. While the file
    // is still downloading those events are noise (play() with no src fires
    // `play`, setting src fires `pause`), so re-assert the loading state after
    // its sync runs. Registered after xsVideoToggle so these fire later.
    function reassertLoading() {
      if (video.__xsSheroLoading) toggle.dataset.state = 'loading';
    }
    video.addEventListener('play', reassertLoading);
    video.addEventListener('pause', reassertLoading);
  }

  function enhanceToggles() {
    if (saveData()) return;
    document.querySelectorAll(SELECTOR).forEach(function (root) {
      var video = root.querySelector('[data-xs-shero-video]');
      var toggle = root.querySelector('[data-xs-video-toggle]');
      if (video && toggle && typeof window.xsVideoToggle === 'function') {
        window.xsVideoToggle(video, toggle);
      }
    });
  }

  function setupVideo(root) {
    var video = root.querySelector('[data-xs-shero-video]');
    var toggle = root.querySelector('[data-xs-video-toggle]');

    if (!video) return;

    video.muted = true; // belt & braces: required for programmatic autoplay

    // Save-Data: poster only. No download, no play button — not even as opt-in.
    if (saveData()) {
      if (toggle) toggle.hidden = true;
      return;
    }

    // Reduced motion: no autoplay, so the play control is the only way in.
    // Show it on the poster. Everyone else waits until the video fades in
    // (.is-video-ready) — a pause button over a still is noise.
    if (reducedMotion()) root.classList.add('is-toggle-visible');

    bindToggle(root, video, toggle);

    var posterReady = false;
    var inView = false;

    function tryAutoplay() {
      if (video.__xsSheroDestroyed || video.__xsAutoplayDecided) return;
      if (!posterReady || !inView) return;
      video.__xsAutoplayDecided = true;

      if (reducedMotion()) return;

      if (shouldSkipAutoplay(root).skip) return;

      playIfAllowed(root, video);
    }

    whenPosterSettled(root, function () {
      posterReady = true;
      tryAutoplay();
    });

    if (!('IntersectionObserver' in window)) {
      inView = true;
      tryAutoplay();
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        inView = entry.isIntersecting;
        if (entry.isIntersecting) tryAutoplay();
      });
    }, { threshold: 0.1 });
    io.observe(root);
    // Stash the instance so xs:section:destroy can disconnect it; otherwise
    // the observer outlives the section in the theme editor.
    video.__xsSheroObserver = io;
  }

  function teardownVideo(video) {
    video.__xsSheroDestroyed = true;
    clearFetchTimeout(video);
    if (video.__xsSheroObserver) {
      video.__xsSheroObserver.disconnect();
      video.__xsSheroObserver = null;
    }
    if (video.__xsSheroAbort) {
      try { video.__xsSheroAbort.abort(); } catch (_) { /* already settled */ }
      video.__xsSheroAbort = null;
    }
    if (video.__xsSheroObjectUrl) {
      try { video.pause(); } catch (_) { /* detached */ }
      URL.revokeObjectURL(video.__xsSheroObjectUrl);
      video.__xsSheroObjectUrl = null;
    }
  }

  /* ---------- Init / theme editor wiring ---------- */

  function initSection(root) {
    if (!root || initialized.has(root)) return;
    initialized.add(root);
    setupVideo(root);
  }

  function initAll() {
    document.querySelectorAll(SELECTOR).forEach(initSection);
  }

  // Start now. This file is deferred, so the document is already parsed when
  // we run; waiting for DOMContentLoaded only held us until every later
  // deferred script (xero-utils, gsap, apps) had finished. The pause toggle
  // helper is attached below once xero-utils exists — video fetch does not wait.
  initAll();

  if (typeof window.xsVideoToggle === 'function') {
    enhanceToggles();
  } else {
    document.addEventListener('DOMContentLoaded', enhanceToggles);
  }

  document.addEventListener('xs:section:init', function (e) {
    var wrapper = document.getElementById('shopify-section-' + e.detail.sectionId);
    if (!wrapper) return;
    var root = wrapper.matches(SELECTOR) ? wrapper : wrapper.querySelector(SELECTOR);
    if (root) initSection(root);
  });

  document.addEventListener('xs:section:destroy', function (e) {
    var wrapper = document.getElementById('shopify-section-' + e.detail.sectionId);
    if (!wrapper) return;
    wrapper.querySelectorAll('[data-xs-shero-video]').forEach(teardownVideo);
  });
})();
